import { onAnalyticsConsent, readAnalyticsConsent } from "./analytics-consent";

/**
 * The ad trackers (Meta Pixel, TikTok Pixel, OpenAI Pixel, Google Ads), held until the
 * visitor accepts the cookie banner, like Clarity and PostHog.
 *
 * They used to be inline scripts in the root document, so every visitor was
 * tracked (and got `_fbp` / `_tt_enable_cookie`) before answering the banner,
 * including the ones who then chose "Essential only" (audit 2026-09-30).
 *
 * - Meta, TikTok and OpenAI are injected only after "granted". Funnel events fired
 *   before that are dropped: `fbq` / `ttq` do not exist yet, and every caller
 *   already guards on them.
 * - Google Ads stays in the root document under Consent Mode: the tag loads
 *   with every storage type "denied" (no cookies, no ad identifiers) and is
 *   switched to "granted" here. That keeps the landing click id through
 *   `url_passthrough` without storing anything before consent.
 *
 * The ids are public (they ship to every browser), hard-coded so a missing
 * deploy variable cannot switch a pixel off silently; env vars override them
 * for staging pixels.
 */

// TikTok Pixel, dataset "MaJourneys web" (ads account 7282692682517037058).
export const TIKTOK_PIXEL_ID =
  (import.meta.env.VITE_TIKTOK_PIXEL_ID as string | undefined) ?? "D2NK843C77U9E0P220DG";

// Meta Pixel, dataset "MJ data" (ad account 981975656976945), created 2026-09-22.
export const META_PIXEL_ID =
  (import.meta.env.VITE_META_PIXEL_ID as string | undefined) ?? "799835012017198";

// OpenAI Measurement Pixel, data source "My first pixel" in ChatGPT Ads
// Manager (account adacct_6abc01179cc8819799114ec1bd7d1792), created 2026-09-30.
export const OPENAI_PIXEL_ID =
  (import.meta.env.VITE_OPENAI_PIXEL_ID as string | undefined) ?? "YZk4wjeYnZoMitbNK6E4KU";

export const TIKTOK_SCRIPT_ID = "mj-tiktok-pixel";
export const META_SCRIPT_ID = "mj-meta-pixel";
export const OPENAI_SCRIPT_ID = "mj-openai-pixel";

// TikTok base code, verbatim from Events Manager except the interpolated id.
// Registers `window.ttq`, loads events.js (allow-listed in server.ts's CSP)
// and records the first page view.
function tiktokSnippet(id: string): string {
  return `!function (w, d, t) {
  w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{},n=document.createElement("script"),n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t,e=document.getElementsByTagName("script")[0],e.parentNode.insertBefore(n,e)};
  ttq.load(${JSON.stringify(id)});
  ttq.page();
}(window, document, 'ttq');`;
}

// Meta base code, verbatim from Events Manager except the interpolated id and
// the `autoConfig` line. Registers `window.fbq`, loads fbevents.js and sends
// the first PageView; fbevents records later History API navigations itself.
//
// `autoConfig` off switches off Meta's automatic events (SubscribedButtonClick
// and page metadata). With it on, every tap ran Meta's button scan over the
// page (hundreds of innerText and querySelectorAll calls), measured on
// majourneys.com at about three quarters of each tap's main-thread time
// (median 21 ms to 6 ms on a fast laptop, several times that on a phone),
// which is what kept INP in "needs improvement". Our conversions are explicit
// fbq('track') calls (meta-pixel.ts) and the server-side API; nothing uses the
// automatic events. Must run before `init`.
function metaSnippet(id: string): string {
  return `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
fbq('set', 'autoConfig', false, ${JSON.stringify(id)});
fbq('init', ${JSON.stringify(id)});
fbq('track', 'PageView');`;
}

// OpenAI base code, verbatim from the Measurement Pixel docs except the
// interpolated id. Registers `window.oaiq`, loads oaiq.min.js (allow-listed in
// server.ts's CSP). It only runs after "granted", so the pixel's own consent
// default (true) is correct. No `user` object: we send no customer data.
function openAiSnippet(id: string): string {
  return `(function (w, d, s, u) {
  if (w.oaiq) return;
  var q = function () { q.q.push(arguments); };
  q.q = [];
  w.oaiq = q;
  var js = d.createElement(s);
  js.async = true;
  js.src = u;
  var f = d.getElementsByTagName(s)[0];
  f.parentNode.insertBefore(js, f);
})(window, document, "script", "https://bzrcdn.openai.com/sdk/oaiq.min.js");
oaiq("init", { pixelId: ${JSON.stringify(id)} });`;
}

/**
 * The Google Ads tag's first commands, for the root document. Consent Mode
 * defaults must be queued before `config`, so they live in the same snippet.
 */
/**
 * The page address Google is given, minus personal data. The tag pings Google
 * with the URL even before consent, and old checkout links carry the guest's
 * name, email and phone in the query; thank-you URLs carry the full booking
 * reference that unlocks cancel and modify. Plain ES5, inlined in the head
 * before `config`; mirrors `scrubUrl` in shared/analytics/scrub-url.ts.
 */
export const GOOGLE_URL_SCRUB = String.raw`function __mjScrub(u){if(!u)return u;u=u.replace(/\b([A-Z]{2,4}-\d{6,})-[A-Za-z0-9]{8,}\b/g,'$1');var re=/([?&])(?:firstName|lastName|fullName|email|phone|transportAddress|address|notes)=[^&#]*&?/,p;do{p=u;u=u.replace(re,'$1');}while(u!==p);return u.replace(/[?&](#|$)/,'$1');}`;

export const GOOGLE_ADS_BOOTSTRAP =
  "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=window.gtag||gtag;" +
  "gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied',wait_for_update:500});" +
  "gtag('set','url_passthrough',true);gtag('set','ads_data_redaction',true);" +
  GOOGLE_URL_SCRUB +
  "gtag('set','page_location',__mjScrub(location.href));gtag('set','page_referrer',__mjScrub(document.referrer));" +
  "gtag('js',new Date());gtag('config','AW-969299995');";

function injectInline(id: string, code: string): void {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.text = code;
  document.head.appendChild(s);
}

function grant(): void {
  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  w.gtag?.("consent", "update", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
  });
  if (TIKTOK_PIXEL_ID) injectInline(TIKTOK_SCRIPT_ID, tiktokSnippet(TIKTOK_PIXEL_ID));
  if (/^\d+$/.test(META_PIXEL_ID)) injectInline(META_SCRIPT_ID, metaSnippet(META_PIXEL_ID));
  if (OPENAI_PIXEL_ID) injectInline(OPENAI_SCRIPT_ID, openAiSnippet(OPENAI_PIXEL_ID));
}

/**
 * Starts the ad trackers when consent is, or later becomes, "granted".
 * Safe to call more than once: each pixel is injected at most once.
 */
export function loadAdPixelsIfConsented(): void {
  if (readAnalyticsConsent() === "granted") {
    grant();
    return;
  }
  const off = onAnalyticsConsent((value) => {
    if (value !== "granted") return;
    grant();
    off();
  });
}
